import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Loader2, FileText, CheckCircle2, AlertCircle, Download, 
  Home, DollarSign, Calendar, User
} from "lucide-react";
import { API } from "@/App";

/**
 * Contract Accept Page
 * Public page for clients to review and accept their agency contract
 * Records immutable audit trail on acceptance
 */
export default function ContractAcceptPage() {
  const { token } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  
  // Form state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  
  useEffect(() => {
    const fetchContract = async () => {
      try {
        const response = await axios.get(`${API}/contract/view/${token}`);
        setContract(response.data);
        setFullName(response.data.client_name || "");
        setEmail(response.data.client_email || "");
        
        if (response.data.status === "accepted") {
          setAccepted(true);
        }
      } catch (err) {
        console.error("Error fetching contract:", err);
        setError(err.response?.data?.detail || "Contract not found or link expired");
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchContract();
    }
  }, [token]);

  const handleAccept = async () => {
    if (!agreeTerms) {
      toast.error("Please confirm you have read and agree to the contract terms");
      return;
    }
    
    if (!fullName.trim()) {
      toast.error("Please enter your full name");
      return;
    }
    
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    
    setAccepting(true);
    try {
      const response = await axios.post(`${API}/contract/accept/${token}`, {
        full_name: fullName.trim(),
        email: email.trim()
      });
      
      toast.success(response.data.message || "Contract accepted successfully!");
      setAccepted(true);
      setContract(prev => ({
        ...prev,
        status: "accepted",
        accepted_at: response.data.accepted_at,
        accepted_by_name: fullName
      }));
    } catch (err) {
      console.error("Error accepting contract:", err);
      toast.error(err.response?.data?.detail || "Failed to accept contract");
    } finally {
      setAccepting(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "Not specified";
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatFee = () => {
    if (!contract) return "";
    const { fee_type, fee_amount, vat_applicable } = contract;
    if (fee_type === "percentage") {
      return `${fee_amount}%${vat_applicable ? " + VAT" : ""}`;
    }
    return formatCurrency(fee_amount) + (vat_applicable ? " + VAT" : "");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading contract...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Contract Not Found</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted || contract?.status === "accepted") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 text-center">
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Contract Accepted</h2>
            <p className="text-slate-600 mb-6">
              Thank you for accepting the agency contract for<br />
              <strong>{contract?.property_address}</strong>
            </p>
            
            {contract?.accepted_at && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800">
                  Accepted by <strong>{contract.accepted_by_name || fullName}</strong><br />
                  on {new Date(contract.accepted_at).toLocaleString('en-GB')}
                </p>
              </div>
            )}
            
            <p className="text-sm text-slate-500">
              A confirmation has been sent to your email. Our team will be in touch shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white py-6">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-2xl font-bold">Goodacres Residential</h1>
          <p className="text-slate-300 text-sm mt-1">Agency Contract Review</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Property Summary */}
        <Card className="mb-6">
          <CardHeader className="border-b bg-slate-50">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Home className="w-5 h-5 text-blue-600" />
              Property Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Property Address</p>
                <p className="font-semibold text-slate-900">{contract?.property_address}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Asking Price</p>
                <p className="font-semibold text-slate-900">{formatCurrency(contract?.asking_price)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Agency Type</p>
                <p className="font-semibold text-slate-900 capitalize">{contract?.agency_type || "Sole"} Agency</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Agency Fee</p>
                <p className="font-semibold text-slate-900">{formatFee()}</p>
              </div>
              {contract?.period_of_sole_agency && (
                <div className="md:col-span-2">
                  <p className="text-sm text-slate-500">Period of Sole Agency</p>
                  <p className="font-semibold text-slate-900">{contract.period_of_sole_agency}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contract Document */}
        {(contract?.contract_pdf_url || contract?.contract_doc_url) && (
          <Card className="mb-6">
            <CardHeader className="border-b bg-slate-50">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-blue-600" />
                Contract Document
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-3">
                  <FileText className="w-10 h-10 text-blue-600" />
                  <div>
                    <p className="font-medium text-slate-900">Agency Contract</p>
                    <p className="text-sm text-slate-500">PDF Document</p>
                  </div>
                </div>
                <a 
                  href={`${API.replace('/api', '')}${contract.contract_pdf_url || contract.contract_doc_url}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <Download className="w-4 h-4" />
                  View Contract (PDF)
                </a>
              </div>
              <p className="text-sm text-slate-500 mt-3">
                Please download and review the full contract document before accepting.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Full Terms & Conditions */}
        <Card className="mb-6">
          <CardHeader className="border-b bg-slate-50">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-blue-600" />
              Terms & Conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-600 mb-4">
              Please read the full terms and conditions below before accepting the contract.
            </p>
            <div 
              className="max-h-96 overflow-y-auto border rounded-lg p-4 bg-white text-sm text-slate-700 leading-relaxed"
              data-testid="terms-conditions-text"
            >
              <h4 className="font-bold text-slate-800 mb-3">AGENCY AGREEMENT - TERMS AND CONDITIONS</h4>
              
              <p className="mb-3"><strong>1. DEFINITIONS</strong></p>
              <p className="mb-2">In this Agreement:</p>
              <ul className="list-disc pl-5 mb-3 space-y-1">
                <li>"The Agent" means Goodacres Residential Ltd</li>
                <li>"The Client" or "Vendor" means the property owner(s) named in this agreement</li>
                <li>"The Property" means the property detailed in this agreement</li>
                <li>"The Purchaser" means any person who enters into a legally binding contract to purchase the Property</li>
              </ul>
              
              <p className="mb-3"><strong>2. AGENT'S OBLIGATIONS</strong></p>
              <p className="mb-2">The Agent agrees to:</p>
              <ul className="list-disc pl-5 mb-3 space-y-1">
                <li>Market the Property using appropriate advertising methods</li>
                <li>Conduct viewings with prospective purchasers</li>
                <li>Negotiate offers on behalf of the Client</li>
                <li>Provide regular updates on marketing activity and viewer feedback</li>
                <li>Liaise with solicitors and other parties to progress the sale</li>
                <li>Comply with all relevant property regulations including Estate Agents Act 1979</li>
              </ul>
              
              <p className="mb-3"><strong>3. CLIENT'S OBLIGATIONS</strong></p>
              <p className="mb-2">The Client agrees to:</p>
              <ul className="list-disc pl-5 mb-3 space-y-1">
                <li>Provide accurate information about the Property</li>
                <li>Allow reasonable access for viewings</li>
                <li>Notify the Agent of any changes to circumstances</li>
                <li>Pay the agreed fee upon completion of sale</li>
                <li>Provide identification documents as required by anti-money laundering regulations</li>
              </ul>
              
              <p className="mb-3"><strong>4. SOLE AGENCY TERMS</strong></p>
              <p className="mb-2">If a Sole Agency is agreed:</p>
              <ul className="list-disc pl-5 mb-3 space-y-1">
                <li>The Agent shall be the only agent instructed to sell the Property during the sole agency period</li>
                <li>The fee is payable if the Property is sold during or after the sole agency period to a purchaser introduced by the Agent</li>
                <li>The fee is also payable if the Property is sold to a purchaser introduced by another agent during the sole agency period</li>
              </ul>
              
              <p className="mb-3"><strong>5. FEES AND PAYMENT</strong></p>
              <ul className="list-disc pl-5 mb-3 space-y-1">
                <li>The fee is calculated as a percentage of the final sale price, as stated in this agreement</li>
                <li>VAT at the prevailing rate will be added to all fees where applicable</li>
                <li>The fee becomes payable upon legal completion of the sale</li>
                <li>Any additional marketing fees agreed are payable as stated</li>
              </ul>
              
              <p className="mb-3"><strong>6. TERMINATION</strong></p>
              <ul className="list-disc pl-5 mb-3 space-y-1">
                <li>Either party may terminate this agreement by giving written notice</li>
                <li>The minimum notice period for sole agency is as stated in this agreement</li>
                <li>If the Property is sold to a purchaser introduced during the agency period, fees remain payable</li>
              </ul>
              
              <p className="mb-3"><strong>7. ANTI-MONEY LAUNDERING</strong></p>
              <p className="mb-3">
                The Agent is required by law to verify the identity of all clients. The Client agrees to provide 
                identification documents (passport or driving licence and proof of address) before marketing can commence.
              </p>
              
              <p className="mb-3"><strong>8. DATA PROTECTION</strong></p>
              <p className="mb-3">
                The Agent will process personal data in accordance with the UK GDPR and Data Protection Act 2018. 
                Data will be used for the purpose of marketing and selling the Property and will not be shared 
                with third parties except where necessary for the sale process.
              </p>
              
              <p className="mb-3"><strong>9. COMPLAINTS</strong></p>
              <p className="mb-3">
                The Agent has a formal complaints procedure available on request. The Agent is a member of 
                The Property Ombudsman scheme for the resolution of disputes.
              </p>
              
              <p className="mb-3"><strong>10. GOVERNING LAW</strong></p>
              <p className="mb-3">
                This Agreement shall be governed by and construed in accordance with English law and both parties 
                agree to submit to the exclusive jurisdiction of the English courts.
              </p>
              
              <p className="mt-4 text-xs text-slate-500">
                Goodacres Residential Ltd is registered in England. Company Registration No: 12345678.
                Registered Office: High Street, Bedford, MK40 1AA.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Acceptance Form */}
        <Card className="mb-6">
          <CardHeader className="border-b bg-slate-50">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-blue-600" />
              Accept Contract
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full legal name"
                  className="mt-1"
                  data-testid="acceptance-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="mt-1"
                  data-testid="acceptance-email-input"
                />
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="agreeTerms"
                    checked={agreeTerms}
                    onCheckedChange={setAgreeTerms}
                    className="mt-0.5"
                    data-testid="acceptance-checkbox"
                  />
                  <label htmlFor="agreeTerms" className="text-sm text-slate-700 cursor-pointer">
                    <strong>I confirm that I have read the Terms & Conditions above and agree to be bound by them.</strong>
                    <p className="mt-1 text-slate-500">
                      By accepting, you are entering into a legally binding agency agreement with Goodacres Residential.
                    </p>
                  </label>
                </div>
              </div>
              
              <Button
                onClick={handleAccept}
                disabled={accepting || !agreeTerms}
                className="w-full mt-6 py-6 text-lg font-semibold bg-green-600 hover:bg-green-700"
                data-testid="accept-contract-btn"
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Accept Contract
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Legal Notice */}
        <div className="text-center text-sm text-slate-500 space-y-2">
          <p>By accepting this contract, your acceptance will be recorded with a secure audit trail including:</p>
          <p className="text-xs">Your name, email address, IP address, timestamp, and device information.</p>
          <p className="text-xs mt-4">
            Sent on: {contract?.sent_at ? new Date(contract.sent_at).toLocaleString('en-GB') : 'N/A'}
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-6 mt-12">
        <div className="max-w-3xl mx-auto px-4 text-center text-sm">
          <p>© {new Date().getFullYear()} Goodacres Residential Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
